import React, { CSSProperties, FC, useEffect, useState, useTransition, useCallback, useLayoutEffect } from 'react'

import { Tab } from '@enuygun/design-system'
import { BusTicket, CarRental, Credit, DepositFull, FlightRotated, Hotels, Transfer } from '@enuygun/icons'
import getConfig from 'next/config'
import { useRouter } from 'next/router'

import {
  BusMobileSearchForm,
  BusSearchForm,
  CarRentalMobileSearchForm,
  FlightMobileSearchForm,
  FlightSearchForm,
  HotelMobileSearchForm,
  HotelSearchForm,
  TransferMobileSearchForm,
  TransferSearchForm,
  DynamicSearchForm,
  DynamicMobileSearchForm,
} from './dynamic'
import { CarRentalWithRentalType, CarRentalBlurGuard } from './CarRentalWrapper'
import { SearchFormAffixWrapper } from 'components/WingieSearchForm/styled'
import { useAdvertState } from 'context/AdvertContext'
import { useAppState } from 'context/AppContext'
import { PRODUCT_CATEGORIES } from 'enums/categories'
import { useIsClient } from 'hooks/useIsClient'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  findTabIndexByCategory,
  getCategoryFromHomepageRef,
  getPreviousCategoryBeforeShow,
  getTabIndexFromHomepageRef,
  resolveHomepageRefParam,
  savePreviousCategoryBeforeSubmit,
} from 'utils/searchForm'
import { busHandleSubmit, carRentalHandleSubmit, flightHandleSubmit, hotelHandleSubmit, transferHandleSubmit } from './Submits'
import { SearchFormMobileContainer } from './SearchForms.styled'
import Affix from 'components/Affix'
import Image from 'next/image'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'
import { DIRECT_FLIGHT_LABEL_EXPERIMENT_KEY, DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY, resolveDirectFlightLabelVariantFromOverride } from 'utils/directFlightLabelExperiment'
const { publicRuntimeConfig } = getConfig()
const processEnv = publicRuntimeConfig?.processEnv
const baseCoreApi = processEnv.NEXT_PUBLIC_CORE_API

//  const SearchFormUserTitle = withESI(SearchFormsTitleComp, 'SearchFormUserTitle', FRAGMENT_URL) as unknown as React.FC<any>

interface SearchFormProps {
  isTablet?: boolean
  /** Otel autocomplete loglarında sayfayı belirtir (HOT-39290). */
  autocompletePage?: string
}

// Safe parse variant from experiment value, returns default if invalid
// API returns string values like "A", "B" instead of numbers
const parseVariant = (value: string | undefined, defaultValue: string): string => {
  if (!value) return defaultValue
  // Value is already a string like "A" or "B"
  return value
}

const getCategoryKeyFromTab = (category: number): string => {
  return (Object.keys(PRODUCT_CATEGORIES) as Array<keyof typeof PRODUCT_CATEGORIES>).find((k) => PRODUCT_CATEGORIES[k] === category) || 'ucak-bileti'
}

const hideCheckboxInSafari = (checkbox: HTMLInputElement, isSafari: boolean) => {
  if (!isSafari) return

  // Checkbox'u gizle
  checkbox.style.display = 'none'
  // Parent element'leri kontrol et ve gizle
  let parent = checkbox.parentElement
  while (parent) {
    // Label veya checkbox container'ı bul
    if (parent.tagName === 'LABEL' || parent.getAttribute('role') === 'checkbox' || parent.classList.contains('checkbox')) {
      parent.style.display = 'none'
      break
    }
    parent = parent.parentElement
  }
}

const handleHotelCheckboxChange = (e: Event, setLogTimeoutId: (id: ReturnType<typeof setTimeout> | null) => void) => {
  const isCheckedByAPI = (e.target as HTMLInputElement).dataset.apiCheck

  if (isCheckedByAPI !== 'true') return

  const logTimeoutId = setTimeout(() => {
    if ((e.target as HTMLInputElement).checked === false) {
      sendTrackerLog({
        category: 'flight',
        page: 'enuygun-homepage',
        event: 'HotelListCheckboxUnChecked',
        message: 'Hotel list checkbox unchecked',
      })
    }
  }, 0)

  setLogTimeoutId(logTimeoutId)
}

const setupHotelCheckboxListener = ({ isSafari, onChange }: { isSafari: boolean; onChange: (e: Event) => void }): HTMLInputElement | null => {
  const showHotelCheckbox = document.querySelector('input[name="showListHotel"]') as HTMLInputElement

  if (!showHotelCheckbox) return null

  // Safari'de checkbox'u gizle
  hideCheckboxInSafari(showHotelCheckbox, isSafari)

  // Safari'de event listener ekleme
  if (!isSafari) {
    showHotelCheckbox.addEventListener('change', onChange)
  }

  return showHotelCheckbox
}

type SearchFormTab = {
  title: React.ReactNode
  component?: React.ElementType | string
  onSubmit?: (...args: any[]) => any
  category?: number
  dataTestId?: string
  icon?: React.ReactNode
  isTablet?: boolean
  isCarRentalTab?: boolean
  isMobile?: boolean
  isAffixedTitle?: string
  noAffixedTitle?: string
  justify?: string
}

type SearchFormTabContentProps = {
  tab: SearchFormTab
  index: number
  activeTab: number
  forceRender: boolean
  market?: string
  language?: string
  expansionLocale?: string
  currencyIso?: string
  variant: string
  delay: number
  isTablet: boolean
  containerRef: React.RefObject<HTMLDivElement>
  flightAutocompleteUrl: string
  carRentalAutocompleteUrl: string
  hotelAutocompleteCore: { baseUrl: string }
  autocompletePage: string
}

const buildOnSubmit = (
  tab: SearchFormTab,
  index: number,
  {
    market,
    delay,
    currencyIso,
    expansionLocale,
    language,
    variant,
  }: {
    market?: string
    delay: number
    currencyIso?: string
    expansionLocale?: string
    language?: string
    variant: string
  }
) => {
  const submit = tab.onSubmit
  if (!submit) return undefined

  return (form) =>
    submit(form, {
      market,
      index,
      delay,
      url: 'https://www.enuygun.com',
      currency: currencyIso,
      pathLocale: expansionLocale,
      language,
      variant,
      productCategory: getCategoryKeyFromTab(tab.category ?? PRODUCT_CATEGORIES['ucak-bileti']),
    })
}

const getAffixKey = (index: number, activeTab: number, forceRender: boolean) => {
  if (index === 0 && activeTab === 0) return `${forceRender}-${index}-${activeTab}`
  return `${index}-${activeTab}`
}

const getTabAutocompleteUrl = (category: number | undefined, flightUrl: string, carRentalUrl: string) => {
  if (category === PRODUCT_CATEGORIES['ucak-bileti']) return flightUrl
  if (category === PRODUCT_CATEGORIES['arac-kiralama']) return carRentalUrl
  return undefined
}

const getDynamicRefParam = (isDynamic: boolean, variant: string) => {
  if (!isDynamic) return 'dynamic_package_hp'
  return variant === 'B' ? 'dynamic_package_hp_b' : 'dynamic_package_hp'
}

const buildDynamicSubmitCallback = (isDynamic: boolean, index: number) => {
  if (!isDynamic) return undefined
  return () => savePreviousCategoryBeforeSubmit(String(index))
}

const renderSearchFormTabComponent = ({
  tab,
  index,
  isTablet,
  onSubmit,
  carRentalAutocompleteUrl,
  flightAutocompleteUrl,
  hotelAutocompleteCore,
  autocompletePage,
  variant,
}: {
  tab: SearchFormTab
  index: number
  isTablet: boolean
  onSubmit: ReturnType<typeof buildOnSubmit>
  carRentalAutocompleteUrl: string
  flightAutocompleteUrl: string
  hotelAutocompleteCore: { baseUrl: string }
  autocompletePage: string
  variant: string
}) => {
  if (!tab.component) return null

  if (tab.isCarRentalTab) {
    //@ts-ignore
    return <tab.component url={carRentalAutocompleteUrl} onSubmit={onSubmit} />
  }

  const isDynamic = tab.category === PRODUCT_CATEGORIES['dinamik']
  const isCarRental = tab.category === PRODUCT_CATEGORIES['arac-kiralama']
  const isHotel = tab.category === PRODUCT_CATEGORIES['otel']

  return (
    <CarRentalBlurGuard enabled={isCarRental}>
      {/* @ts-ignore */}
      <tab.component
        isTablet={isTablet}
        onSubmit={isDynamic ? undefined : onSubmit}
        onSubmitCallback={buildDynamicSubmitCallback(isDynamic, index)}
        refParam={getDynamicRefParam(isDynamic, variant)}
        title={null}
        url={getTabAutocompleteUrl(tab.category, flightAutocompleteUrl, carRentalAutocompleteUrl)}
        autocompleteCore={isHotel ? hotelAutocompleteCore : undefined}
        autocompletePage={isHotel ? autocompletePage : undefined}
        isMobile={tab.isMobile}
        monthlySearchForm={isCarRental}
      />
    </CarRentalBlurGuard>
  )
}

/** Panel content only — Tab.Panel must stay a direct child of Tab for title/icon. */
const SearchFormTabContent: FC<SearchFormTabContentProps> = (props) => {
  const {
    tab,
    index,
    activeTab,
    forceRender,
    market,
    language,
    expansionLocale,
    currencyIso,
    variant,
    delay,
    isTablet,
    containerRef,
    flightAutocompleteUrl,
    carRentalAutocompleteUrl,
    hotelAutocompleteCore,
    autocompletePage,
  } = props

  const onSubmit = buildOnSubmit(tab, index, {
    market,
    delay,
    currencyIso,
    expansionLocale,
    language,
    variant,
  })

  return (
    <Affix containerRef={containerRef} market={market}>
      <SearchFormAffixWrapper key={getAffixKey(index, activeTab, forceRender)} market={market} $variant={variant}>
        {renderSearchFormTabComponent({
          tab,
          index,
          isTablet,
          onSubmit,
          carRentalAutocompleteUrl,
          flightAutocompleteUrl,
          hotelAutocompleteCore,
          autocompletePage,
          variant,
        })}
      </SearchFormAffixWrapper>
    </Affix>
  )
}

const SearchForms: FC<SearchFormProps> = ({ isTablet = false, autocompletePage = 'homepage' }) => {
  const isClient = useIsClient()
  const ref = React.useRef<HTMLDivElement>(null)
  const dynamicTabRef = React.useRef<HTMLElement | null>(null)
  const isMobile = useIsMobile()
  const { market, currency, language, expansionLocale, endpoint, searchFormVariant, directFlightLabelVariant: directFlightLabelVariantFromContext } = useAppState()
  const { setCategory, category } = useAdvertState()
  const [forceRender, setForceRender] = React.useState(false)

  const { query } = useRouter()

  // URL'den override parametresini al (örn: ?test_variant=B)
  const getWindowSearchParam = (key: string): string | null => {
    if (globalThis.window) {
      return new URLSearchParams(globalThis.window.location.search).get(key)
    }
    return null
  }
  const queryVariant = getWindowSearchParam('test_variant')
  const queryDirectFlightOverride = query?.[DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY]
  const directFlightLabelQueryOverride = typeof queryDirectFlightOverride === 'string' ? queryDirectFlightOverride : getWindowSearchParam(DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY)
  const directFlightLabelVariant = resolveDirectFlightLabelVariantFromOverride(directFlightLabelQueryOverride, directFlightLabelVariantFromContext || 'A')

  // Get variant from server-side experiment data or URL override
  const apiVariant = parseVariant(searchFormVariant, 'A')
  const variant = queryVariant || apiVariant

  const [, startTransition] = useTransition()
  // ?ref=otel|arac|... ilk boyamada doğru sekmede olsun (Tab defaultActive + controlled active)
  const initialRefTabIndex = getTabIndexFromHomepageRef(query.ref as string | string[] | undefined)
  const [activeTab, setActiveTab] = useState(() => (initialRefTabIndex >= 0 ? initialRefTabIndex : 0))
  /** URL/localStorage sekme seçimi yalnızca bir kez; aksi halde tab tıklamasını geri zorlar (mobile flicker). */
  const appliedInitialTabRef = React.useRef(false)
  const delay = isMobile ? 400 : 300
  const shouldUseMobileView = isMobile || isTablet

  const getPathSegment = (market: string | undefined) => {
    if (market === 'tr') return 'arama'
    return 'flights/search'
  }

  const pathSegment = getPathSegment(market)
  const baseUrl = endpoint ?? ''
  const marketPrefix = market === 'tr' ? '/ucak-bileti' : ''
  const $url = `${baseUrl}${marketPrefix}/${pathSegment}/trip-autocomplete/`
  // Araç kiralama autocomplete — landing FlightSearchDialog ile aynı taban (pickUpLocation / popüler rotalar API)
  const $carRentalAutocompleteUrl = `${baseUrl}/arac-kiralama`
  const hotelAutocompleteCore = { baseUrl: 'https://hot-autocomplete-api.enuygun.com' }

  const tabs = React.useMemo(() => {
    const desktopTabs = [
      {
        title: 'Uçak bileti',
        component: FlightSearchForm,
        onSubmit: flightHandleSubmit,
        category: PRODUCT_CATEGORIES['ucak-bileti'],
        dataTestId: 'search-form-flight-tab',
        icon: <FlightRotated style={{ marginRight: '4px' }} width={20} height={20} />,
      },
      {
        title: 'Otel',
        component: HotelSearchForm,
        onSubmit: hotelHandleSubmit,
        category: PRODUCT_CATEGORIES['otel'],
        dataTestId: 'search-form-hotel-tab',
        icon: <Hotels style={{ marginRight: '8px' }} width={20} height={20} />,
        isTablet,
      },
      {
        title: 'Otobüs',
        component: BusSearchForm,
        onSubmit: busHandleSubmit,
        category: PRODUCT_CATEGORIES['otobus-bileti'],
        dataTestId: 'search-form-bus-tab',
        icon: <BusTicket style={{ marginRight: '4px' }} width={20} height={20} />,
      },
      {
        title: 'Uçak+Otel',
        isAffixedTitle: 'Paket Ara',
        noAffixedTitle: "En uygun uçak ve otel paketleri Enuygun'da!",
        icon: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginRight: '8px',
              position: 'relative',
              height: '20px',
              justifyContent: 'center',
              alignItems: 'flex-start',
              overflow: 'visible',
            }}
            data-testid="ucak-otel-tab-icon-wrapper"
          >
            <FlightRotated width={16} height={16} style={{ marginBottom: '-4px' }} />
            <Hotels width={16} height={16} />
          </div>
        ),
        component: DynamicSearchForm,
        category: PRODUCT_CATEGORIES['dinamik'],
        justify: 'flex-start',
        dataTestId: 'dynamic-search-form',
      },
      {
        title: 'Araç kiralama',
        component: CarRentalWithRentalType,
        onSubmit: carRentalHandleSubmit,
        category: PRODUCT_CATEGORIES['arac-kiralama'],
        dataTestId: 'search-form-car-rental-tab',
        icon: <CarRental style={{ marginRight: '8px' }} width={20} height={20} />,
        isCarRentalTab: true,
      },
      {
        title: 'Transfer',
        component: TransferSearchForm,
        onSubmit: transferHandleSubmit,
        category: PRODUCT_CATEGORIES['transfer'],
        dataTestId: 'search-form-transfer-tab',
        icon: <Transfer style={{ marginRight: '8px' }} width={20} height={20} />,
      },
    ]

    const mobileTabs = [
      {
        title: 'Uçak',
        component: FlightMobileSearchForm,
        onSubmit: flightHandleSubmit,
        category: PRODUCT_CATEGORIES['ucak-bileti'],
        icon: <FlightRotated style={{ marginRight: '3px' }} width={22} height={22} />,
        dataTestId: 'search-form-flight-tab-mobile',
      },
      {
        title: 'Otel',
        component: HotelMobileSearchForm,
        onSubmit: hotelHandleSubmit,
        category: PRODUCT_CATEGORIES['otel'],
        icon: <Hotels style={{ marginRight: '5px', width: '22px' }} width={22} height={22} />,
        dataTestId: 'search-form-hotel-tab-mobile',
      },
      {
        title: 'Otobüs',
        component: BusMobileSearchForm,
        onSubmit: busHandleSubmit,
        category: PRODUCT_CATEGORIES['otobus-bileti'],
        dataTestId: 'search-form-bus-tab-mobile',
        icon: <BusTicket style={{ marginRight: '3px', width: '20px' }} width={22} height={22} />,
      },
      {
        title: 'Uçak+Otel',
        component: DynamicMobileSearchForm,
        category: PRODUCT_CATEGORIES['dinamik'],
        dataTestId: 'dynamic-search-form-mobile',
        icon: (
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: '8px', position: 'relative' }}>
            <FlightRotated width={16} height={16} style={{ marginBottom: '-4px' }} />
            <Hotels width={16} height={16} />
          </div>
        ),
      },
      {
        title: 'Araç',
        component: CarRentalMobileSearchForm,
        onSubmit: carRentalHandleSubmit,
        category: PRODUCT_CATEGORIES['arac-kiralama'],
        dataTestId: 'search-form-car-rental-tab-mobile',
        icon: <CarRental style={{ marginRight: '5px', width: '18px' }} width={20} height={20} />,
      },
      {
        title: 'Transfer',
        component: TransferMobileSearchForm,
        onSubmit: transferHandleSubmit,
        category: PRODUCT_CATEGORIES['transfer'],
        dataTestId: 'search-form-transfer-tab-mobile',
        icon: <Transfer style={{ marginRight: '5px', width: '20px' }} width={20} height={20} />,
      },
      {
        title: (
          <a
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            href="https://www.enuygunfinans.com/kredi/?ref=enuygun-anasayfa-tab"
            rel="noreferrer"
            data-testid="search-form-credit-tab"
          >
            Kredi
          </a>
        ),
        component: 'span',
        dataTestId: 'search-form-credit-tab',
        icon: <Credit style={{ marginRight: '5px', width: '18px' }} width={20} height={20} />,
      },
      {
        title: (
          <a
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            href="https://www.enuygunfinans.com/mevduat/?ref=enuygun-anasayfa-tab"
            rel="noreferrer"
            data-testid="search-form-deposit-tab"
          >
            Mevduat
          </a>
        ),
        component: 'span',
        dataTestId: 'search-form-deposit-tab',
        icon: <DepositFull style={{ marginRight: '5px', width: '18px' }} width={20} height={20} />,
      },
    ]

    /*    
    // Uçak+Otel tabını sadece onurkaya query parametresi varsa göster
    const filteredDesktopTabs = showUcakOtelTab ? desktopTabs : desktopTabs.filter((tab) => tab.title !== 'Uçak+Otel')

    const filteredMobileTabs = showUcakOtelTab ? mobileTabs : mobileTabs.filter((tab) => tab.title !== 'Uçak+Otel')
 */
    return {
      desktop: desktopTabs,
      mobile: mobileTabs,
    }
  }, [market, currency?.iso, isMobile, isTablet])

  const $tabs = shouldUseMobileView ? tabs.mobile : tabs.desktop

  // Kullanıcı tıklamasında design-system Tab container'ı zaten smooth kaydırıyor. Burada her activeTab
  // değişiminde scrollIntoView yapmak (temizlenmeyen iç timeout ile) hızlı gel-git'te eski sekmeye geri
  // kaydırıp mobilde flicker yaratıyordu; bu yüzden yalnızca ilk seçimde ve sadece yatay kaydırıyoruz.
  const initialTabScrollTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>()
  const centerInitialTab = useCallback((index: number) => {
    if (index <= 0) return
    clearTimeout(initialTabScrollTimeoutRef.current)
    let attempts = 0
    // Tab strip hydrate/layout sonrası scroll edilebilir hale geliyor; hazır olana kadar kısa aralıklarla dene
    const tryCenter = () => {
      const tabContainer = ref.current?.querySelector<HTMLElement>('[data-testid$="-tab-left-wrapper"]')
      const tab = tabContainer?.querySelectorAll<HTMLElement>('[role="tab"]')[index]
      if (tabContainer && tab && tabContainer.scrollWidth > tabContainer.clientWidth) {
        tabContainer.scrollTo({ left: tab.offsetLeft - tabContainer.offsetWidth / 2 + tab.offsetWidth / 2 })
        return
      }
      if (++attempts < 20) initialTabScrollTimeoutRef.current = setTimeout(tryCenter, 150)
    }
    initialTabScrollTimeoutRef.current = setTimeout(tryCenter, 100)
  }, [])

  useEffect(() => {
    // İlk seçim bir kez uygulanır; her $tabs/re-render'da tekrar zorlamak tab tıklamasında flicker yapar.
    if (appliedInitialTabRef.current) return

    const refParam = resolveHomepageRefParam(query.ref as string | string[] | undefined)
    const refCategory = getCategoryFromHomepageRef(refParam)
    if (refCategory) {
      const refTabIndex = findTabIndexByCategory($tabs, refCategory)
      if (refTabIndex !== -1) {
        appliedInitialTabRef.current = true
        // Hydration bitmeden context güncellemesi Suspense'i client render'a düşürüyordu → transition
        startTransition(() => {
          setActiveTab(refTabIndex)
          if (setCategory) {
            setCategory(refCategory)
          }
        })
        centerInitialTab(refTabIndex)
        return
      }
    }

    // localStorage yalnızca client hydrate sonrası bir kez
    if (!isClient) return

    appliedInitialTabRef.current = true
    const newActiveTab = query.form ? category && PRODUCT_CATEGORIES[category] : getPreviousCategoryBeforeShow(true)
    startTransition(() => {
      setActiveTab(newActiveTab || 0)
    })
    centerInitialTab(newActiveTab || 0)
  }, [query.form, query.ref, isClient, $tabs, setCategory, category, centerInitialTab])

  const wrapperStyle: CSSProperties = isMobile
    ? { background: '#F8FAF9', borderRadius: '30px 30px 0px 0px', padding: '14px 16px' }
    : { maxWidth: 1240, padding: 0, margin: '0 auto' }

  // İlk seçimden (ref/localStorage) sonra tab strip'i bir kez ortalar; unmount'ta timeout temizlenir.
  React.useEffect(() => () => clearTimeout(initialTabScrollTimeoutRef.current), [])

  const findLastForm = useCallback(
    (title: string) => {
      // @ts-ignore
      const form = $tabs.find((tab) => tab.title === title)
      const $category = ((form?.category && Object.keys(PRODUCT_CATEGORIES).find((k) => PRODUCT_CATEGORIES[k] === form?.category)) ||
        'ucak-bileti') as keyof typeof PRODUCT_CATEGORIES
      if (form?.category !== undefined && setCategory) {
        setCategory($category)
      }
    },
    [$tabs, setCategory]
  )

  // Sadece uçuş sekmesinde Safari checkbox DOM senkronu için forceRender kullanılıyor.
  // Key'e tüm sekmelerde yazılınca ~67ms sonra araç kiralama formu yeniden mount olup ilk autocomplete seçimini sıfırlıyordu.
  useLayoutEffect(() => {
    if (activeTab !== 0) return
    const id = setTimeout(() => {
      setForceRender((v) => !v)
    }, 67)
    return () => clearTimeout(id)
  }, [activeTab])

  // Variant view impression - kaç kullanıcı hangi variantı gördü (A ve B her ikisi loglanır)
  const variantViewLoggedRef = React.useRef(false)
  useEffect(() => {
    if (variantViewLoggedRef.current || !isClient) return
    variantViewLoggedRef.current = true
    const requestId = getAnalyticsRequestId()
    sendTrackerLog({
      category: 'enuygun-homepage',
      page: 'enuygun-homepage',
      type: 'search_form_variant_view',
      event: 'search_form_variant_impression',
      message: `Search form variant ${variant} viewed`,
      search_form_variant: variant?.toLowerCase() || 'a',
      requestId,
      market: market || 'tr',
      isMobile: !!isMobile,
    })
  }, [variant, isClient, isMobile, market])

  const directFlightLabelViewLoggedRef = React.useRef(false)
  useEffect(() => {
    if (directFlightLabelViewLoggedRef.current || !isClient || activeTab !== 0) return
    directFlightLabelViewLoggedRef.current = true
    const requestId = getAnalyticsRequestId()
    sendTrackerLog({
      category: 'enuygun-homepage',
      page: 'enuygun-homepage',
      type: 'direct_flight_label_variant_view',
      event: 'direct_flight_label_variant_impression',
      message: `Direct flight label variant ${directFlightLabelVariant} viewed`,
      direct_flight_label_variant: directFlightLabelVariant.toLowerCase(),
      experiment_key: DIRECT_FLIGHT_LABEL_EXPERIMENT_KEY,
      requestId,
      market: market || 'tr',
      isMobile: !!isMobile,
    })
  }, [directFlightLabelVariant, isClient, isMobile, market, activeTab])

  useEffect(() => {
    // Sadece desktop FlightSearchForm aktifken çalış
    if (activeTab !== 0 || isMobile) return

    // Safari kontrolü
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    let showHotelCheckbox: HTMLInputElement | null = null
    let logTimeoutId: ReturnType<typeof setTimeout> | null = null
    let observer: MutationObserver | null = null

    const onChange = (e: Event) => {
      handleHotelCheckboxChange(e, (id) => {
        logTimeoutId = id
      })
    }

    const trySetup = () => {
      showHotelCheckbox = setupHotelCheckboxListener({ isSafari, onChange })
      return !!showHotelCheckbox
    }

    // İlk kontrol
    if (!trySetup() && ref.current) {
      // Checkbox henüz render edilmemişse, MutationObserver ile bekle
      observer = new MutationObserver(() => {
        if (trySetup() && observer) {
          observer.disconnect()
        }
      })

      observer.observe(ref.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      if (logTimeoutId) {
        clearTimeout(logTimeoutId)
      }
      if (showHotelCheckbox && !isSafari) {
        showHotelCheckbox.removeEventListener('change', onChange)
      }
      if (observer) {
        observer.disconnect()
      }
    }
  }, [activeTab, isMobile, forceRender])

  // Dynamic tab için badge konumlandırması
  useEffect(() => {
    if (!ref.current) return

    const findDynamicTab = () => {
      // Genel tablist içinde bul
      const tablist = ref.current?.querySelector('[role="tablist"]')
      if (tablist) {
        const tabs = tablist.querySelectorAll('[role="tab"]')
        // Desktop'ta index 2, mobile'da index 2 (Uçak+Otel tab'ı)
        const dynamicTabIndex = 3
        const dynamicTab = tabs[dynamicTabIndex] as HTMLElement
        if (dynamicTab) return dynamicTab

        // Text içeriğine göre bul (fallback)
        for (const tab of tabs) {
          const htmlTab = tab as HTMLElement
          if (htmlTab.textContent?.includes('Uçak+Otel')) {
            return htmlTab
          }
        }
      }

      // Tab container içinde index ile bul
      const tabContainer = ref.current?.querySelector('[data-testid$="-tab-left-wrapper"]')
      if (tabContainer) {
        const tabs = tabContainer.querySelectorAll('[role="tab"]')
        const dynamicTabIndex = 3
        const dynamicTab = tabs[dynamicTabIndex] as HTMLElement
        if (dynamicTab) return dynamicTab
      }

      return null
    }

    const updateTabRef = () => {
      const tab = findDynamicTab()
      if (tab) {
        dynamicTabRef.current = tab
      }
    }

    // İlk kontrol
    updateTabRef()

    // MutationObserver ile DOM değişikliklerini izle
    const observer = new MutationObserver(() => {
      updateTabRef()
    })

    if (ref.current) {
      observer.observe(ref.current, {
        childList: true,
        subtree: true,
        attributes: false,
      })
    }

    // Scroll ve resize event'lerinde de güncelle
    window.addEventListener('scroll', updateTabRef, true)
    window.addEventListener('resize', updateTabRef)

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', updateTabRef, true)
      window.removeEventListener('resize', updateTabRef)
    }
  }, [activeTab, forceRender, isMobile])

  return (
    <SearchFormMobileContainer style={wrapperStyle} ref={ref} isTablet={isTablet} $variant={variant}>
      <React.Fragment>
        {isMobile && (
          <Image
            style={{ pointerEvents: 'none', opacity: 0.1, objectFit: 'cover', width: '100%', height: '100%', borderRadius: '30px' }}
            sizes="(max-width: 768px) 16vw, 100vw"
            src={'https://cdn2.enuygun.com/media/lib/165x300/uploads/image/lcp-background-mobile-compres-62417.png'}
            priority
            fill
            unoptimized
            alt="Enuygun search form arka plan"
          />
        )}
        {/* @ts-ignore */}
        <React.Suspense fallback={null}>
          <Tab
            shape="separated"
            defaultActive={initialRefTabIndex >= 0 ? initialRefTabIndex : undefined}
            active={activeTab}
            onClick={(e, key) => {
              if (typeof key === 'number') {
                setActiveTab(key)
              }

              // Tab change - ne kadarı tab değiştirdi (A ve B her ikisi loglanır)
              if (typeof key === 'number' && key !== activeTab && $tabs[key]) {
                const fromTabName = typeof $tabs[activeTab]?.title === 'string' ? $tabs[activeTab].title : 'unknown'
                const toTabName = typeof $tabs[key]?.title === 'string' ? $tabs[key].title : 'unknown'
                const toTabCategory = $tabs[key]?.category
                const requestId = getAnalyticsRequestId()
                sendTrackerLog({
                  category: 'enuygun-homepage',
                  page: 'enuygun-homepage',
                  type: 'search_form_tab_change',
                  event: 'search_form_tab_switch',
                  message: 'Search form tab changed',
                  search_form_variant: variant?.toLowerCase() || 'a',
                  from_tab_index: activeTab,
                  to_tab_index: key,
                  from_tab_name: fromTabName,
                  to_tab_name: toTabName,
                  product_category: typeof toTabCategory === 'number' ? getCategoryKeyFromTab(toTabCategory) : undefined,
                  requestId,
                  market: market || 'tr',
                  isMobile: !!isMobile,
                })
              }

              // Scroll pozisyonunu kaydet
              const scrollY = window.scrollY

              if (e?.target && 'innerText' in e.target) {
                findLastForm(e.target.innerText as string)
              }

              // Scroll pozisyonunu restore et
              requestAnimationFrame(() => {
                window.scrollTo(0, scrollY)
              })
            }}
            dataTestId="search-form"
            isMobile={shouldUseMobileView}
            isTablet={isTablet || false}
          >
            {$tabs.map((tab, i) => (
              <Tab.Panel key={tab.dataTestId ?? `category-${tab.category}`} panelKey={i} icon={tab.icon} unmount={false} justify="flex-center" title={tab.title}>
                {/* Landing HomePageSearchForms gibi: unmount=false (design-system default) — sekmeler DOM’da kalır, araç kiralama ilk popüler seçim remount yüzünden boş kalmaz */}
                <SearchFormTabContent
                  tab={tab}
                  index={i}
                  activeTab={activeTab}
                  forceRender={forceRender}
                  market={market}
                  language={language}
                  expansionLocale={expansionLocale}
                  currencyIso={currency?.iso}
                  variant={variant}
                  delay={delay}
                  isTablet={isTablet}
                  containerRef={ref}
                  flightAutocompleteUrl={$url}
                  carRentalAutocompleteUrl={$carRentalAutocompleteUrl}
                  hotelAutocompleteCore={hotelAutocompleteCore}
                  autocompletePage={autocompletePage}
                />
              </Tab.Panel>
            ))}
          </Tab>
        </React.Suspense>
      </React.Fragment>
    </SearchFormMobileContainer>
  )
}

export default SearchForms
